/**
 * Full-screen Spa Ops kiosk view.
 * Designed for an always-on iPad in the spa area, showing today's calendar.
 * When new bookings arrive they pulse until tapped/acknowledged.
 * Uses AuthContext (dashboard session), NOT the guest/iPad session.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, RefreshCw, Sparkles, Calendar as CalendarIcon, Check, Bell, ChevronLeft, ChevronRight, MessageSquare, Heart, User, List as ListIcon, Columns, Volume2, VolumeX, CalendarPlus } from "lucide-react";
import { api, fileUrl, formatErr } from "@/lib/api";
import { useAuth, } from "@/context/AuthContext";
import { useBranding } from "@/lib/branding";
import { useBookingNotifications } from "@/lib/useBookingNotifications";
import { useLiveEvents } from "@/lib/useLiveEvents";
import { useAppTz, dayKeyInTz, hmInTz, fmtTime, zonedIsoFromWallTime } from "@/lib/tz";
import { getServerNow } from "@/lib/serverClock";
import SystemClock from "@/components/SystemClock";
import { Button } from "@/components/ui/button";
import SpaOpsQuickBookDialog from "./SpaOpsQuickBookDialog";
import SpaOpsBookingActions from "./SpaOpsBookingActions";
import ServiceDashboard from "./ServiceDashboard";
import HealthWarning from "@/components/HealthWarning";
import { useTrackView } from "@/lib/analyticsTracker";

const POLL_MS = 20000;
const ACK_KEY = "spa_ops_ack_ids_v1"; // legacy — kept only to unblock older loads; no longer read/written
const MODE_KEY = "spa_ops_view_mode_v1"; // 'list' | 'calendar' | 'hybrid'
const RANGE_KEY = "spa_ops_range_v1";    // 'today' | 'week'
const MUTE_KEY = "spa_ops_muted_v1";
const HOURS = Array.from({ length: 17 }, (_, i) => 7 + i); // 07:00 .. 23:00

function loadAckSet() {
  try { return new Set(JSON.parse(localStorage.getItem(ACK_KEY) || "[]")); } catch { return new Set(); }
}
function saveAckSet(s) {
  try { localStorage.setItem(ACK_KEY, JSON.stringify(Array.from(s))); } catch { /* ignore */ }
}

function dayKey(d, tz = null) {
  // Fixed Feb 2026: was `d.toISOString().slice(0, 10)` which returned the UTC
  // date — off by a day for anyone whose local midnight straddles the UTC
  // date line. Now anchored to the app timezone so day-bucketing agrees with
  // the tile positioning and the booking-actions dialog.
  return dayKeyInTz(d.toISOString(), tz);
}
function startOfDay(d) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }
function startOfWeek(d) { const x = startOfDay(d); const wd = (x.getDay() + 6) % 7; x.setDate(x.getDate() - wd); return x; }

export default function SpaOpsView() {
  const navigate = useNavigate();
  const { admin, logout } = useAuth();
  const { spaName, boatName } = useBranding();
  const appTz = useAppTz();
  useTrackView("spa_ops");
  const [muted, setMuted] = useState(() => localStorage.getItem(MUTE_KEY) === "1");
  const notify = useBookingNotifications({ appName: "Spa Ops", boatName, muted });

  const [day, setDay] = useState(() => startOfDay(getServerNow()));
  const [bookings, setBookings] = useState([]);
  const [crew, setCrew] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [ack, setAck] = useState(loadAckSet());
  const [now, setNow] = useState(getServerNow());
  const [mode, setMode] = useState(() => localStorage.getItem(MODE_KEY) || "hybrid");
  const [range, setRange] = useState(() => localStorage.getItem(RANGE_KEY) || "today");
  const [liveOnline, setLiveOnline] = useState(false);
  const latestCreatedAt = useRef(null);
  const endedNotified = useRef(new Set());

  useEffect(() => { localStorage.setItem(MODE_KEY, mode); }, [mode]);
  useEffect(() => { localStorage.setItem(RANGE_KEY, range); }, [range]);
  useEffect(() => { localStorage.setItem(MUTE_KEY, muted ? "1" : "0"); }, [muted]);

  // Spa Ops kiosk multi-session check. Every 15 s we ask the backend if our
  // per-device session token is still valid. If an admin has kicked THIS
  // iPad from the Connected Devices panel, the backend returns 401 and we
  // sign out locally — no full-page reload, no stale UI. Skips gracefully
  // when no session token is present (older login flows, or the session was
  // revoked in a prior tab and already wiped from storage).
  useEffect(() => {
    if (admin?.role !== "spa_ops") return;
    let cancelled = false;
    const poll = async () => {
      const t = localStorage.getItem("yacht_spa_ops_session_token");
      if (!t) return;
      try {
        await api.post("/public/spa-ops/session-check", { session_token: t });
      } catch (e) {
        if (cancelled) return;
        if (e?.response?.status === 401) {
          toast.error("This kiosk was signed out by the crew.");
          logout();
          navigate("/admin/login");
        }
      }
    };
    // Immediate on-mount check + 15-second cadence so a device that was
    // kicked while backgrounded evicts on the next tab focus.
    poll();
    const id = setInterval(poll, 15000);
    const onVis = () => { if (document.visibilityState === "visible") poll(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { cancelled = true; clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
  }, [admin?.role, logout, navigate]);

  const isSpaOps = admin?.role === "spa_ops";
  const isPantry = admin?.role === "pantry";
  const isKiosk = isSpaOps || isPantry;
  // Every SpaOps operator is a crew-management role, so photos always
  // flow through here. Guest devices strip image_path server-side.
  const canSeePhotos = !!admin?.role;
  // On-the-spot booking permission — Manager+ always allowed, Supervisor/kiosk
  // roles when explicitly granted. The seeded Spa Ops + Pantry kiosk accounts
  // are auto-granted on backend startup so a factory-fresh install works
  // without a manager step.
  const canQuickBook = ["developer", "admin", "manager"].includes(admin?.role)
    || (["supervisor", "spa_ops", "pantry"].includes(admin?.role) && !!admin?.can_create_manual_bookings);

  // Quick-book state — guests/treatments/crew are preloaded so the dialog opens instantly.
  const [quickBookOpen, setQuickBookOpen] = useState(false);
  const [quickBookAt, setQuickBookAt] = useState(null);
  // Optional seed — when the operator taps a CANCELLED booking on the
  // calendar we pre-fill the treatment / crew / duration so rebooking
  // the freed-up slot is a one-tap flow. `null` when the dialog opens
  // via the fresh "+" tap on an empty slot.
  const [quickBookSeed, setQuickBookSeed] = useState(null);
  const [qbGuests, setQbGuests] = useState([]);
  const [qbTreatments, setQbTreatments] = useState([]);

  // Pantry SPA ↔ SERVICE mode toggle. Persisted in localStorage so a
  // shared iPad wakes up in the last-picked mode after backgrounding.
  // Spa Ops and higher roles never see the toggle — service mode is a
  // pantry-crew workflow.
  const [serviceMode, setServiceMode] = useState(() => localStorage.getItem("pantry_service_mode") === "1");
  useEffect(() => { localStorage.setItem("pantry_service_mode", serviceMode ? "1" : "0"); }, [serviceMode]);

  // Preload the booking-picker data once when quick-book is available. Also
  // fetches the settings (prep_time_min / open_time / close_time) so both
  // calendar hotspots AND the quick-book dialog can grey out slots that fall
  // inside the prep buffer of an existing booking — the previous version
  // ignored prep_time_min so the "+" hotspots appeared right next to booked
  // tiles and the datetime-local input accepted any time.
  const [settings, setSettings] = useState(null);

  // Kiosk hero background — a role-scoped photo the admin uploads in
  // Settings so Spa Ops and Pantry iPads read visually distinct at
  // a glance. When a photo is set we opt into the same `body.ipad-cover`
  // CSS treatment guest cabins already use (dark navy scrim, dark-glass
  // cards, opaque dialogs, white text) so a bright deck photo turns into
  // an ambient dark backdrop instead of washing the whole UI out.
  // NOTE: this MUST come after the `settings` state declaration above —
  // otherwise webpack production build fails with a TDZ error
  // ("can't access lexical declaration 'settings' before initialization")
  // on every kiosk refresh, causing the constant reload loop.
  const heroPath = isKiosk
    ? (isPantry
        ? (settings?.pantry_hero_image_path || null)
        : (settings?.spa_ops_hero_image_path || null))
    : null;
  useEffect(() => {
    if (heroPath) document.body.classList.add("ipad-cover");
    return () => document.body.classList.remove("ipad-cover");
  }, [heroPath]);

  useEffect(() => {
    if (!canQuickBook) return;
    (async () => {
      try {
        const [tr, ch, st] = await Promise.all([
          api.get("/public/treatments"),
          api.get("/admin/charters"),
          api.get("/admin/settings").catch(() => api.get("/public/settings")),
        ]);
        setQbTreatments(tr.data || []);
        setSettings(st.data || null);
        const rows = [];
        // Only surface guests from the ACTIVE trip — the previous version
        // walked every charter (past, present, future) so the booking
        // dialog + pantry Service dashboard listed dozens of legacy names
        // like "Mr RC · Boss Trip". The active-charter model on this yacht
        // guarantees exactly one row with `active: true` at any time so we
        // filter on that and drop the rest.
        for (const c of (ch.data || [])) {
          if (!c || !c.active) continue;
          for (const g of (c.guests || [])) {
            if (g.guest_id) rows.push({
              ...g,
              charter_name: c.name,
              charter_arrival: c.arrival,
              charter_departure: c.departure,
            });
          }
        }
        setQbGuests(rows);
      } catch { /* silent — dialog will show empty state */ }
    })();
  }, [canQuickBook]);

  const openQuickBook = useCallback((at) => {
    if (!canQuickBook) return;
    setQuickBookAt(at);
    // Clear any lingering seed from a "rebook-cancelled" tap so a fresh
    // empty-slot tap doesn't inherit the previous cancellation's crew /
    // treatment / duration selections.
    setQuickBookSeed(null);
    setQuickBookOpen(true);
  }, [canQuickBook]);

  // Redirect anyone without a dashboard session.
  useEffect(() => { if (!admin) navigate("/admin/login", { replace: true }); }, [admin, navigate]);

  const rangeParams = useMemo(() => {
    if (range === "week") {
      const start = startOfWeek(day);
      const end = new Date(start); end.setDate(end.getDate() + 7);
      return { start: start.toISOString(), end: end.toISOString() };
    }
    const start = startOfDay(day);
    const end = new Date(start); end.setDate(end.getDate() + 1);
    return { start: start.toISOString(), end: end.toISOString() };
  }, [day, range]);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [{ data: bs }, { data: cs }] = await Promise.all([
        api.get("/admin/bookings", { params: { start: rangeParams.start, end: rangeParams.end, order: "asc" } }),
        api.get("/admin/crew"),
      ]);
      setBookings(bs || []);
      setCrew(cs || []);
      if (bs && bs.length) {
        latestCreatedAt.current = bs.reduce((mx, b) => (b.created_at > mx ? b.created_at : mx), latestCreatedAt.current || "");
      }
    } catch (e) {
      // 401 → force back to the sign-in screen. The prior code only called
      // `logout()` which flipped `admin` to null, but this component
      // continued rendering until the top-level redirect useEffect fired —
      // meanwhile subsequent renders threw on `admin?.role` reads. Explicit
      // navigate closes the gap.
      if (e.response?.status === 401) { logout(); navigate("/admin/login", { replace: true }); return; }
      toast.error("Could not load spa schedule");
    } finally { if (!silent) setLoading(false); }
  }, [rangeParams, logout, navigate]);

  const pollNew = useCallback(async () => {
    if (!latestCreatedAt.current) return;
    try {
      const { data } = await api.get("/admin/bookings", { params: { since: latestCreatedAt.current, order: "asc", sort: "created_at" } });
      if (data && data.length) {
        const nextAck = new Set(ack);
        data.forEach((b) => {
          nextAck.delete(b.id);
          notify(b, "new");
          toast.success(`New booking · ${b.guest_name}`, {
            description: `${b.treatment_name} · ${new Date(b.start_at).toLocaleString()}`,
            duration: 10000,
          });
        });
        setAck(nextAck); saveAckSet(nextAck);
        latestCreatedAt.current = data.reduce((mx, b) => (b.created_at > mx ? b.created_at : mx), latestCreatedAt.current);
        setRefreshing(true); await load(true); setRefreshing(false);
      }
    } catch { /* silent */ }
  }, [ack, load, notify]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [day, range]);
  useEffect(() => { const t = setInterval(pollNew, POLL_MS); return () => clearInterval(t); }, [pollNew]);
  // 15-second tick — now-line + strikethroughs. Reads the server clock so
  // a device with a drifted battery clock still shows the correct "now" on
  // the calendar.
  useEffect(() => { const t = setInterval(() => setNow(getServerNow()), 15000); return () => clearInterval(t); }, []);

  // End-of-session detection — quiet descending chime as each booking wraps up.
  useEffect(() => {
    const nowMs = now.getTime();
    bookings.forEach((b) => {
      if (b.status === "cancelled") return;
      if (!b.end_at && !b.duration_min) return;
      const endMs = b.end_at
        ? new Date(b.end_at).getTime()
        : new Date(b.start_at).getTime() + (b.duration_min || 60) * 60000;
      // Fire once when we cross the end time (within the last 60 s window).
      if (endMs <= nowMs && (nowMs - endMs) < 60000 && !endedNotified.current.has(b.id)) {
        endedNotified.current.add(b.id);
        notify(b, "end");
        toast(`Session ended · ${b.guest_name}`, {
          description: b.treatment_name,
          duration: 6000,
        });
      }
    });
  }, [now, bookings, notify]);

  // Real-time WebSocket wiring — instant updates for create/update/cancel/delete.
  useLiveEvents(useCallback((ev) => {
    if (!ev || !ev.type) return;
    if (ev.type === "hello") { setLiveOnline(true); return; }
    if (!ev.type.startsWith("booking.")) return;
    const b = ev.booking || {};
    // Only refresh if the event's booking is on the day currently shown.
    // Uses the app timezone (same as tile bucketing) so an out-of-tz booking
    // doesn't skip the refresh.
    const startDay = b.start_at ? dayKeyInTz(b.start_at, appTz) : null;
    const showingDay = dayKey(day, appTz);
    if (startDay !== showingDay) return;
    if (ev.type === "booking.created") {
      notify(b, "new");
      toast.success(`New booking · ${b.guest_name}`, {
        description: `${b.treatment_name} · ${new Date(b.start_at).toLocaleString()}`,
        duration: 10000,
      });
    } else if (ev.type === "booking.confirmed") {
      // Someone else (or another device) confirmed — quiet toast, no chime.
      // The tile stops flashing automatically once `confirmed_at` populates.
      toast.success(`Confirmed by ${b.confirmed_by_name || "Crew"} · ${b.guest_name}`, {
        description: b.treatment_name,
        duration: 4000,
      });
    } else if (ev.type === "booking.cancelled") {
      notify(b, "cancel");
      toast.warning(`Cancelled · ${b.guest_name}`, {
        description: `${b.treatment_name} · ${new Date(b.start_at).toLocaleString()}`,
        duration: 8000,
      });
    } else if (ev.type === "booking.deleted") {
      toast(`Removed · ${b.guest_name}`);
    }
    // Any relevant event → refetch to stay in perfect sync.
    load(true);
  }, [day, load, notify]));

  const grouped = useMemo(() => {
    // Merge couples pairs and sort by time.
    const seen = new Set();
    const rows = [];
    const list = [...bookings];
    for (const b of list) {
      if (b.couple_booking_id) {
        if (seen.has(b.couple_booking_id)) continue;
        seen.add(b.couple_booking_id);
        const pair = list.filter((x) => x.couple_booking_id === b.couple_booking_id).sort((a, c) => (a.created_at || "").localeCompare(c.created_at || ""));
        const primary = pair[0]; const partner = pair[1] || null;
        rows.push({ ...primary, isCouple: !!partner, primary, partner, guest_name: partner ? `${primary.guest_name} & ${partner.guest_name}` : primary.guest_name });
      } else {
        rows.push({ ...b, isCouple: false, primary: b, partner: null });
      }
    }
    rows.sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
    return rows;
  }, [bookings]);

  // A booking flashes until an authorised user hits "Confirm" — the flash is
  // now driven ENTIRELY by the server-side `confirmed_at` field so every device
  // stops flashing the moment any authorised operator confirms. Cancelled
  // bookings never flash.
  const isNew = (b) => !b.confirmed_at && b.status !== "cancelled";

  // Kept as a compat shim for the old per-tile "acknowledge" prop plumbing —
  // now delegates to the actions dialog which is where confirmation actually
  // happens.
  const acknowledge = (b) => { setActiveBookingId(b.id); setActionsOpen(true); };

  const [actionsOpen, setActionsOpen] = useState(false);
  const [activeBookingId, setActiveBookingId] = useState(null);
  const activeBooking = useMemo(() => {
    if (!activeBookingId) return null;
    // Look up the raw booking (not the grouped couple wrapper) so the dialog
    // works on both individual and couple bookings.
    return bookings.find((b) => b.id === activeBookingId)
      || grouped.find((b) => b.id === activeBookingId)
      || null;
  }, [activeBookingId, bookings, grouped]);
  const openBookingActions = useCallback((b) => {
    // When the operator taps a CANCELLED booking on the calendar, the
    // most natural intent is "let me rebook this slot" — not "let me
    // manage this dead reservation". Reroute to the QuickBook dialog
    // pre-seeded with the cancelled booking's start/crew/treatment so
    // the crew can drop a fresh guest into the freed-up slot in seconds.
    // The old actions dialog remains reachable via long-press or the
    // audit log for anyone who genuinely needs to inspect it.
    if (b?.status === "cancelled" && canQuickBook) {
      const at = b.start_at ? new Date(b.start_at) : null;
      setQuickBookAt(at);
      setQuickBookSeed({
        treatment_id: b.treatment_id || "",
        crew_id: b.crew_id || "",
        duration_min: b.duration_min || null,
      });
      setQuickBookOpen(true);
      return;
    }
    setActiveBookingId(b.id);
    setActionsOpen(true);
  }, [canQuickBook]);

  // Drag-drop reschedule handler for the calendar views. The tile's `dragstart`
  // stores the booking id; on drop we compute the target datetime, PUT it via
  // the update endpoint (which re-runs clash detection), and toast the result.
  const rescheduleBooking = useCallback(async (bid, when) => {
    if (!canQuickBook) return;
    try {
      await api.put(`/admin/bookings/${bid}`, { start_at: when.toISOString() });
      toast.success(`Moved to ${when.toTimeString().slice(0, 5)}`);
      load(true);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Reschedule failed");
    }
  }, [canQuickBook, load]);

  const unackCount = grouped.filter(isNew).length;

  const shiftDay = (delta) => {
    const n = new Date(day);
    n.setDate(n.getDate() + (range === "week" ? delta * 7 : delta));
    setDay(startOfDay(n));
  };

  const isToday = dayKey(day) === dayKey(startOfDay(getServerNow()));

  // Next / currently-in-progress appointment across the currently-shown day.
  const nextAppt = useMemo(() => {
    const active = grouped.filter((b) => b.status !== "cancelled");
    // In progress = started but not ended
    const inProgress = active.find((b) => {
      const s = new Date(b.start_at).getTime();
      const e = new Date(b.end_at || b.start_at).getTime() + (b.end_at ? 0 : (b.duration_min || 60) * 60000);
      return s <= now.getTime() && e > now.getTime();
    });
    if (inProgress) return { row: inProgress, state: "in_progress" };
    // Otherwise the first upcoming today
    const upcoming = active.find((b) => new Date(b.start_at).getTime() > now.getTime());
    if (upcoming) return { row: upcoming, state: "upcoming" };
    return null;
  }, [grouped, now]);

  const formatCountdown = (mins) => {
    const m = Math.max(0, Math.floor(mins));
    if (m < 1) return "starting now";
    if (m < 60) return `in ${m} min`;
    const h = Math.floor(m / 60), r = m % 60;
    return r === 0 ? `in ${h} h` : `in ${h} h ${r} min`;
  };

  return (
    <div className="h-screen flex flex-col bg-background overflow-hidden relative" data-testid="spa-ops-view">
      {/* Kiosk hero background — a role-scoped photo the admin uploads in
          Settings so Spa Ops and Pantry iPads read visually distinct at
          a glance. Uses the same dark-navy scrim treatment as guest cabin
          iPads (via `body.ipad-cover` toggled in a useEffect above) so
          bright deck photos become an ambient backdrop instead of washing
          the whole UI out. */}
      {isKiosk && heroPath && (
        <div className="absolute inset-0 z-0 pointer-events-none" aria-hidden data-testid="spa-ops-hero-bg">
          <img src={fileUrl(heroPath)} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-primary/80" />
        </div>
      )}
      <div className="relative z-10 h-full flex flex-col">
      {/* Bar */}
      <header className="border-b border-border bg-card/85 backdrop-blur-sm shrink-0">
        <div className="max-w-full mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <Sparkles className="h-5 w-5 text-accent shrink-0" />
            <div className="min-w-0">
              <div className="overline text-primary/60 text-[10px]" data-testid="spa-ops-kiosk-role">
                {isPantry ? "Pantry · Kiosk" : "Spa Ops · Kiosk"}
              </div>
              <div className="font-display text-lg sm:text-xl leading-none truncate">{spaName || "Onboard Spa"} <span className="text-muted-foreground text-sm hidden sm:inline">· {boatName || ""}</span></div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-end">
            <div className="hidden lg:inline-flex items-center gap-1.5 text-[10px] tracking-widest text-muted-foreground uppercase" title={liveOnline ? "Live · connected" : "Reconnecting…"}>
              <span className={`inline-flex h-2 w-2 rounded-full ${liveOnline ? "bg-emerald-500" : "bg-amber-500 animate-pulse"}`} data-testid="spa-ops-live-dot" />
              {liveOnline ? "Live" : "…"}
            </div>
            {unackCount > 0 && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-accent/15 border border-accent/40 rounded-full" data-testid="spa-ops-unack-badge">
                <span className="flex h-2.5 w-2.5 relative">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-accent" />
                </span>
                <span className="text-xs font-medium text-accent">{unackCount} new</span>
              </div>
            )}
            {/* Yacht system clock — replaces the plain `now.toLocaleTimeString`
                so drift is visible and every scheduling calc reads from the
                same source. */}
            <SystemClock className="hidden md:inline-flex" compact />
            {/* SPA ↔ SERVICE toggle — Pantry-only. Persists in
                localStorage so a shared iPad wakes up in the last-picked
                mode. SPA mode = the calendar view below. SERVICE mode
                = full-screen Guest Preferences dashboard. */}
            {isPantry && (
              <div className="inline-flex border border-border rounded-full overflow-hidden" data-testid="pantry-mode-toggle">
                <button
                  onClick={() => setServiceMode(false)}
                  className={`px-3 h-8 text-xs uppercase tracking-widest ${!serviceMode ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
                  data-testid="pantry-mode-spa"
                >Spa</button>
                <button
                  onClick={() => setServiceMode(true)}
                  className={`px-3 h-8 text-xs uppercase tracking-widest ${serviceMode ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
                  data-testid="pantry-mode-service"
                >Service</button>
              </div>
            )}
            {/* Manual booking — the persistent + entry point Aug 2026 UX
                pass added so operators don't have to hunt for a dashed
                "+" hotspot on the calendar. Opens the same guest-flavoured
                dialog with a cabin → guest → treatment → date → slot
                wizard. Only visible when the role has quick-book rights. */}
            {canQuickBook && !(isPantry && serviceMode) && (
              <Button
                size="sm"
                onClick={() => openQuickBook(new Date())}
                data-testid="spa-ops-new-booking"
                className="bg-accent hover:bg-accent/90 text-accent-foreground shrink-0"
              >
                <CalendarPlus className="h-3.5 w-3.5 mr-1.5" /> New booking
              </Button>
            )}
            <Button
              variant="outline" size="sm"
              onClick={() => setMuted((m) => !m)}
              title={muted ? "Unmute alerts" : "Mute alerts"}
              data-testid="spa-ops-mute"
            >
              {muted ? <VolumeX className="h-3.5 w-3.5 text-destructive" /> : <Volume2 className="h-3.5 w-3.5" />}
            </Button>
            <Button variant="outline" size="sm" onClick={() => load(true)} disabled={loading || refreshing} data-testid="spa-ops-refresh">
              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
            </Button>
            {/* Sign out is prominent (accent tint) and never gets clipped off
                the right edge — the header now wraps on narrower iPad widths
                so no button falls off-screen. */}
            <Button
              size="sm"
              onClick={() => { logout(); navigate("/admin/login"); }}
              data-testid="spa-ops-logout"
              className="bg-primary hover:bg-primary/90 text-primary-foreground shrink-0"
            >
              <LogOut className="h-3.5 w-3.5 mr-1.5" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      {/* Pantry SERVICE mode short-circuits the calendar view entirely.
          Renders the Guest Preferences dashboard full-bleed so the crew
          can move between guests / categories without the calendar
          chrome getting in the way. */}
      {isPantry && serviceMode ? (
        <ServiceDashboard guests={qbGuests} />
      ) : (
      <main className="flex-1 min-h-0 flex flex-col px-6 py-4 overflow-hidden">
        {/* Compact "Next appointment" card */}
        {isToday && range === "today" && nextAppt && (() => {
          const b = nextAppt.row;
          const startMs = new Date(b.start_at).getTime();
          const diffMin = (startMs - now.getTime()) / 60000;
          const isImminent = nextAppt.state === "upcoming" && diffMin <= 15;
          const inProgress = nextAppt.state === "in_progress";
          return (
            <div
              className={`mb-3 relative overflow-hidden border px-5 py-3 shrink-0 ${
                inProgress ? "border-primary/60 bg-primary text-primary-foreground"
                  : isImminent ? "border-accent bg-accent/15"
                  : "border-border bg-card"
              }`}
              data-testid="spa-ops-next-card"
              data-state={nextAppt.state}
            >
              <div className="flex items-center gap-5 flex-wrap">
                <div className="min-w-[95px]">
                  <div className={`overline text-[9px] ${inProgress ? "text-primary-foreground/70" : "text-accent"}`}>
                    {inProgress ? "In progress" : "Next"}
                  </div>
                  <div className="font-display text-3xl leading-none" data-testid="spa-ops-next-time">
                    {fmtTime(b.start_at, appTz)}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {b.isCouple && <Heart className="h-3.5 w-3.5 text-accent" strokeWidth={2.5} />}
                    {canSeePhotos && (
                      <span className="flex -space-x-2 mr-1">
                        {[b.primary, b.partner].filter(Boolean).map((g) => (
                          g?.guest_image_path ? (
                            <img key={g.id} src={fileUrl(g.guest_image_path)} alt="" className={`w-8 h-8 rounded-full object-cover border-2 ${inProgress ? "border-primary" : "border-card"}`} />
                          ) : (
                            <span key={g.id} className={`w-8 h-8 rounded-full ${inProgress ? "bg-primary-foreground/10 border-primary" : "bg-muted border-card"} border-2 flex items-center justify-center`}>
                              <User className={`h-3.5 w-3.5 ${inProgress ? "text-primary-foreground/60" : "text-muted-foreground"}`} />
                            </span>
                          )
                        ))}
                      </span>
                    )}
                    <span className="font-display text-xl md:text-2xl leading-tight" data-testid="spa-ops-next-guest">{b.guest_name}</span>
                    <span className={`text-sm md:text-base ${inProgress ? "text-primary-foreground/85" : "text-foreground/80"}`} data-testid="spa-ops-next-treatment">
                      · {b.treatment_name}
                      {b.crew_id && (
                        <span className={inProgress ? "text-primary-foreground/60" : "text-muted-foreground"}> · {crew.find((c) => c.id === b.crew_id)?.name || "Any"}</span>
                      )}
                    </span>
                  </div>
                </div>
                <div className={`font-display text-2xl md:text-3xl ${isImminent && !inProgress ? "text-accent" : ""}`} data-testid="spa-ops-next-countdown">
                  {inProgress
                    ? "ends " + fmtTime(b.end_at || new Date(startMs + (b.duration_min || 60) * 60000).toISOString(), appTz)
                    : formatCountdown(diffMin)}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Day/Week nav + mode toggle */}
        <div className="flex items-center justify-between mb-3 gap-4 flex-wrap shrink-0">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => shiftDay(-1)} data-testid="spa-ops-prev-day"><ChevronLeft className="h-4 w-4" /></Button>
            <div className="min-w-[220px] text-center">
              <div className="overline text-accent text-[10px]">
                {range === "week" ? "Week" : (isToday ? "Today" : "Scheduled")}
              </div>
              <div className="font-display text-xl md:text-2xl leading-none" data-testid="spa-ops-day-label">
                {range === "week"
                  ? (() => {
                      const s = startOfWeek(day); const e = new Date(s); e.setDate(e.getDate() + 6);
                      return `${s.toLocaleDateString(undefined, { month: "short", day: "numeric" })} — ${e.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
                    })()
                  : day.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => shiftDay(1)} data-testid="spa-ops-next-day"><ChevronRight className="h-4 w-4" /></Button>
            {(range === "week" ? dayKey(startOfWeek(day)) !== dayKey(startOfWeek(getServerNow())) : !isToday) && (
              <Button variant="ghost" size="sm" onClick={() => setDay(startOfDay(getServerNow()))} className="text-accent" data-testid="spa-ops-today">
                {range === "week" ? "This week" : "Today"}
              </Button>
            )}
            {/* Range sub-toggle — only meaningful when a calendar column is visible */}
            {(mode === "calendar" || mode === "hybrid") && (
              <div className="ml-2 inline-flex items-center border border-border rounded-full overflow-hidden" role="tablist" data-testid="spa-ops-range-toggle">
                {[
                  { id: "today", label: "Today" },
                  { id: "week", label: "Week" },
                ].map((r) => (
                  <button
                    key={r.id}
                    onClick={() => setRange(r.id)}
                    className={`px-3 py-1.5 uppercase tracking-widest text-[10px] transition-colors ${
                      range === r.id ? "bg-primary text-primary-foreground" : "hover:bg-muted"
                    }`}
                    data-testid={`spa-ops-range-${r.id}`}
                    aria-pressed={range === r.id}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="text-xs text-muted-foreground flex items-center gap-3">
            <div className="inline-flex items-center border border-border rounded-full overflow-hidden" role="tablist" data-testid="spa-ops-mode-toggle">
              {[
                { id: "list", label: "List", icon: <ListIcon className="h-3.5 w-3.5" /> },
                { id: "calendar", label: "Calendar", icon: <CalendarIcon className="h-3.5 w-3.5" /> },
                { id: "hybrid", label: "Split", icon: <Columns className="h-3.5 w-3.5" /> },
              ].map((m) => (
                <button
                  key={m.id}
                  onClick={() => setMode(m.id)}
                  className={`px-3 py-1.5 flex items-center gap-1.5 uppercase tracking-widest text-[10px] transition-colors ${
                    mode === m.id ? "bg-primary text-primary-foreground" : "hover:bg-muted"
                  }`}
                  data-testid={`spa-ops-mode-${m.id}`}
                  aria-pressed={mode === m.id}
                >
                  {m.icon} <span className="hidden md:inline">{m.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Mode-aware content — fills remaining height, scrolls internally */}
        <div className="flex-1 min-h-0" data-testid="spa-ops-content">
          {loading ? (
            <div className="text-center text-muted-foreground py-24">Loading schedule…</div>
          ) : grouped.length === 0 && !(canQuickBook && (mode === "calendar" || mode === "hybrid")) ? (
            <div className="border border-dashed border-border p-16 text-center text-muted-foreground h-full flex flex-col items-center justify-center" data-testid="spa-ops-empty">
              <CalendarIcon className="h-8 w-8 mb-3 opacity-50" />
              No appointments this {range === "week" ? "week" : "day"}.
              {canQuickBook && (
                <div className="text-[11px] mt-3 opacity-70">
                  Switch to Calendar view to tap-to-book on any day.
                </div>
              )}
            </div>
          ) : mode === "list" ? (
            <div className="h-full overflow-y-auto pr-2">
              <ListView items={grouped} canSeePhotos={canSeePhotos} crew={crew} ack={ack} isNew={isNew} acknowledge={acknowledge} onOpenActions={openBookingActions} />
            </div>
          ) : mode === "calendar" ? (
            <div className="h-full overflow-y-auto">
              {range === "week"
                ? <WeekCalendarView weekStart={startOfWeek(day)} items={grouped} canSeePhotos={canSeePhotos} crew={crew} isNew={isNew} acknowledge={acknowledge} onOpenActions={openBookingActions} now={now} canQuickBook={canQuickBook} onSlotClick={openQuickBook} canEdit={canQuickBook} onReschedule={rescheduleBooking} prepMin={Number(settings?.prep_time_min) || 0} appTz={appTz} />
                : <CalendarView day={day} items={grouped} canSeePhotos={canSeePhotos} crew={crew} isNew={isNew} acknowledge={acknowledge} onOpenActions={openBookingActions} now={now} isToday={isToday} canQuickBook={canQuickBook} onSlotClick={openQuickBook} canEdit={canQuickBook} onReschedule={rescheduleBooking} prepMin={Number(settings?.prep_time_min) || 0} appTz={appTz} />}
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 h-full" data-testid="spa-ops-hybrid">
              <div className="lg:col-span-2 min-h-0 overflow-y-auto pr-2">
                <ListView items={grouped} canSeePhotos={canSeePhotos} crew={crew} ack={ack} isNew={isNew} acknowledge={acknowledge} onOpenActions={openBookingActions} compact appTz={appTz} />
              </div>
              <div className="lg:col-span-3 min-h-0 overflow-y-auto">
                {range === "week"
                  ? <WeekCalendarView weekStart={startOfWeek(day)} items={grouped} canSeePhotos={canSeePhotos} crew={crew} isNew={isNew} acknowledge={acknowledge} onOpenActions={openBookingActions} now={now} canQuickBook={canQuickBook} onSlotClick={openQuickBook} canEdit={canQuickBook} onReschedule={rescheduleBooking} prepMin={Number(settings?.prep_time_min) || 0} appTz={appTz} />
                  : <CalendarView day={day} items={grouped} canSeePhotos={canSeePhotos} crew={crew} isNew={isNew} acknowledge={acknowledge} onOpenActions={openBookingActions} now={now} isToday={isToday} canQuickBook={canQuickBook} onSlotClick={openQuickBook} canEdit={canQuickBook} onReschedule={rescheduleBooking} prepMin={Number(settings?.prep_time_min) || 0} appTz={appTz} />}
              </div>
            </div>
          )}
        </div>
        {canQuickBook && (mode === "calendar" || mode === "hybrid") && (
          <div className="mt-2 text-[10px] uppercase tracking-widest text-muted-foreground text-right shrink-0" data-testid="spa-ops-quickbook-hint">
            <span className="inline-flex items-center gap-1"><CalendarPlus className="h-3 w-3 text-accent" /> Tap a dashed <span className="text-accent font-medium">+</span> in the calendar to book on-the-spot</span>
          </div>
        )}
      </main>
      )}

      <SpaOpsQuickBookDialog
        open={quickBookOpen}
        onOpenChange={setQuickBookOpen}
        initialAt={quickBookAt}
        initialSeed={quickBookSeed}
        guests={qbGuests}
        treatments={qbTreatments}
        crew={crew}
        bookings={bookings}
        settings={settings}
        onCreated={() => load(true)}
      />
      <SpaOpsBookingActions
        open={actionsOpen}
        onOpenChange={setActionsOpen}
        booking={activeBooking}
        treatments={qbTreatments}
        crew={crew}
        onChanged={() => load(true)}
      />
      </div>
    </div>
  );
}

// Convert a click's y-pixel offset inside an hour grid into a Date on the given
// day. Snap to 5-minute increments so bookings line up with the spa schedule.
function slotDateFromClick(e, day, hourFirst, rowHeight, tz = null) {
  const rect = e.currentTarget.getBoundingClientRect();
  const y = Math.max(0, Math.min(e.clientY - rect.top, rect.height - 1));
  const totalMin = (y / rect.height) * (HOURS.length * 60);
  let hour = hourFirst + Math.floor(totalMin / 60);
  let minute = Math.round((totalMin % 60) / 5) * 5;
  if (minute === 60) { hour += 1; minute = 0; }
  // Yacht-tz wall clock, not browser-local — otherwise a drop on the
  // "10:00" row from a UTC laptop lands as 10:00 UTC = 13:00 EEST on
  // the yacht and the tile "jumps" by the tz offset.
  if (tz) return new Date(zonedIsoFromWallTime(day, hour, minute, tz));
  const d = new Date(day);
  d.setHours(hour, minute, 0, 0);
  return d;
}

// ---------- Weekly calendar view ----------
function WeekCalendarView({ weekStart, items, canSeePhotos, crew, isNew, acknowledge, onOpenActions, now, canQuickBook, onSlotClick, canEdit, onReschedule, prepMin = 0, appTz = null }) {
  const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return d; });
  const rowHeight = 44;
  const halfHeight = rowHeight / 2;
  const totalHeight = HOURS.length * rowHeight;
  const first = HOURS[0];
  const todayKey = dayKey(startOfDay(getServerNow()), appTz);
  // Pre-build 30-min slot descriptors so we can overlay tap hotspots.
  const slots = [];
  HOURS.forEach((h) => { slots.push({ hour: h, minute: 0 }); slots.push({ hour: h, minute: 30 }); });
  const nowPct = (() => {
    const h = now.getHours(); const m = now.getMinutes();
    const total = (HOURS[HOURS.length - 1] + 1 - first) * 60;
    const cur = (h - first) * 60 + m;
    return Math.max(0, Math.min(100, (cur / total) * 100));
  })();

  // Compute whether a 30-min slot on a given day already overlaps a booking OR
  // falls inside the prep-time buffer of a nearby booking. The buffer expands
  // each busy interval by `prepMin` on both sides so operators can't schedule
  // walk-ins into the therapist's turnaround time. Cancelled bookings ignored.
  const isSlotBusy = (d, hour, minute, dayItems) => {
    const slotStart = new Date(d); slotStart.setHours(hour, minute, 0, 0);
    const slotEnd = new Date(slotStart.getTime() + 30 * 60000);
    const buf = Number(prepMin) * 60000;
    return dayItems.some((b) => {
      if (b.status === "cancelled") return false;
      const s = new Date(b.start_at).getTime() - buf;
      const e = (b.end_at ? new Date(b.end_at).getTime() : new Date(b.start_at).getTime() + (b.duration_min || 60) * 60000) + buf;
      return s < slotEnd.getTime() && e > slotStart.getTime();
    });
  };

  return (
    <div className="bg-card border border-border" data-testid="spa-ops-week">
      {/* Day headers */}
      <div className="grid border-b border-border" style={{ gridTemplateColumns: `48px repeat(7, 1fr)` }}>
        <div />
        {days.map((d) => {
          const isDayToday = dayKey(d, appTz) === todayKey;
          return (
            <div key={d.toISOString()} className={`text-center py-2 border-l border-border ${isDayToday ? "bg-accent/10" : ""}`}>
              <div className={`text-[10px] uppercase tracking-widest ${isDayToday ? "text-accent" : "text-muted-foreground"}`}>
                {d.toLocaleDateString(undefined, { weekday: "short" })}
              </div>
              <div className={`font-display text-lg leading-none ${isDayToday ? "text-accent" : ""}`}>
                {d.getDate()}
              </div>
            </div>
          );
        })}
      </div>
      {/* Grid body */}
      <div className="grid" style={{ gridTemplateColumns: `48px repeat(7, 1fr)` }}>
        {/* Hour column */}
        <div>
          {HOURS.map((h) => (
            <div key={h} className="text-[10px] tracking-widest uppercase text-muted-foreground text-right pr-1 relative" style={{ height: rowHeight }}>
              <span className="absolute -top-1.5 right-1 bg-card px-1">{String(h).padStart(2, "0")}:00</span>
            </div>
          ))}
        </div>
        {/* Day columns */}
        {days.map((d) => {
          const dk = dayKey(d, appTz);
          // Bucket bookings by the SAME tz used for hour positioning so a
          // booking stored in a foreign offset (e.g. `+02:00`) never lands
          // in the wrong day column. Was `(b.start_at || "").slice(0, 10)`
          // which took the raw ISO date part and produced Aliya's Fri-21
          // ghost tile even though the true instant was Thu 20 14:00.
          const dayItems = items.filter((b) => dayKeyInTz(b.start_at || "", appTz) === dk);
          const isDayToday = dk === todayKey;
          const onColClick = (e) => {
            if (!canQuickBook) return;
            if (e.target.closest("button")) return; // click landed on an appointment tile
            const when = slotDateFromClick(e, d, HOURS[0], rowHeight, appTz);
            onSlotClick && onSlotClick(when);
          };
          return (
            <div
              key={dk}
              className={`relative border-l border-border ${isDayToday ? "bg-accent/5" : ""} ${canQuickBook ? "cursor-pointer hover:bg-accent/5" : ""}`}
              style={{ height: totalHeight }}
              onClick={onColClick}
              onDragOver={(e) => { if (canEdit) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}}
              onDrop={(e) => {
                if (!canEdit || !onReschedule) return;
                e.preventDefault();
                // Safari silently rejects non-standard MIME types on
                // `setData` — a custom "text/booking-id" comes back as ""
                // on Safari 16+ (Manager + Spa iPad kiosk both live here).
                // Read "text/plain" first, fall back to the legacy custom
                // type so any pre-cached admin sessions still work.
                const bid = e.dataTransfer.getData("text/plain") || e.dataTransfer.getData("text/booking-id");
                if (!bid) return;
                const when = slotDateFromClick(e, d, HOURS[0], rowHeight, appTz);
                onReschedule(bid, when);
              }}
              role={canQuickBook ? "button" : undefined}
              aria-label={canQuickBook ? "Tap to book on this day" : undefined}
              data-testid={`spa-ops-week-col-${dk}`}
            >
              {HOURS.map((h, i) => (
                <div key={h} className="absolute left-0 right-0 border-t border-border/50" style={{ top: i * rowHeight }} />
              ))}
              {isDayToday && (
                <div className="absolute left-0 right-0 z-10 pointer-events-none" style={{ top: `${nowPct}%` }}>
                  <div className="h-px bg-destructive/70" />
                  <div className="absolute -left-1 -top-1 h-2 w-2 rounded-full bg-destructive" />
                </div>
              )}
              {/* Per-30-min tap hotspots — dashed "+" always visible so an iPad
                  (touch-only) shows the same affordance as a laptop. */}
              {canQuickBook && slots.map(({ hour, minute }) => {
                if (isSlotBusy(d, hour, minute, dayItems)) return null;
                const topPx = (hour - first) * rowHeight + (minute === 30 ? halfHeight : 0);
                return (
                  <button
                    key={`hs-${hour}-${minute}`}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      const when = new Date(d);
                      when.setHours(hour, minute, 0, 0);
                      onSlotClick && onSlotClick(when);
                    }}
                    className="absolute left-0.5 right-0.5 border border-dashed border-accent/20 hover:border-accent hover:bg-accent/10 active:bg-accent/25 flex items-center justify-center text-accent/40 hover:text-accent active:text-accent transition-all text-lg font-light"
                    style={{ top: topPx + 1, height: halfHeight - 2 }}
                    aria-label={`Book on ${d.toLocaleDateString(undefined, { weekday: "short" })} at ${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`}
                    title={`Book at ${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`}
                    data-testid={`spa-ops-week-slot-${dk}-${hour}-${minute}`}
                  >
                    +
                  </button>
                );
              })}
              {dayItems.map((b) => {
                // Hour+minute in the SAME zone the day column uses, so the
                // vertical offset matches the day the tile is drawn on.
                const { hour: h, minute: m } = hmInTz(b.start_at, appTz);
                if (h < first || h > HOURS[HOURS.length - 1]) return null;
                const topPx = (h - first) * rowHeight + (m / 60) * rowHeight;
                const heightPx = Math.max(18, ((b.duration_min || 60) / 60) * rowHeight - 2);
                const highlighted = isNew(b);
                const cancelled = b.status === "cancelled";
                const confirmed = !!b.confirmed_at;
                const startLabel = fmtTime(b.start_at, appTz);
                return (
                  <button
                    key={b.id}
                    draggable={canEdit && !cancelled}
                    onDragStart={(e) => {
                      e.stopPropagation();
                      // Safari-compatible MIME. Also set the legacy type
                      // so a page mid-drag during the fix rollout doesn't
                      // break. `text/plain` is the ONLY type Safari
                      // guarantees on cross-element drops.
                      e.dataTransfer.setData("text/plain", b.id);
                      e.dataTransfer.setData("text/booking-id", b.id);
                      e.dataTransfer.effectAllowed = "move";
                    }}
                    onClick={(e) => { e.stopPropagation(); onOpenActions ? onOpenActions(b) : (highlighted && acknowledge(b)); }}
                    className={`absolute left-0.5 right-0.5 border text-left overflow-hidden transition-all z-10 ${
                      highlighted ? "border-accent bg-accent/20 animate-pulse"
                        : confirmed ? "border-emerald-500/40 bg-emerald-500/5"
                        : b.isCouple ? "border-accent/50 bg-accent/10"
                        : "border-primary/30 bg-primary/5 hover:border-primary/60"
                    } ${cancelled ? "opacity-30 line-through" : ""} ${canEdit && !cancelled ? "cursor-move" : ""}`}
                    style={{ top: topPx + 1, height: heightPx }}
                    data-testid={`spa-ops-week-appt-${b.id}`}
                    title={`${startLabel} · ${b.guest_name} · ${b.treatment_name}${confirmed ? ` · Confirmed by ${b.confirmed_by_name}` : ""}`}
                  >
                    <div className="px-1 py-0.5 text-[10px] leading-tight">
                      <div className="font-medium truncate flex items-center gap-0.5">
                        {b.isCouple && <Heart className="h-2.5 w-2.5 text-accent flex-shrink-0" strokeWidth={2.5} />}
                        {confirmed && <Check className="h-2.5 w-2.5 text-emerald-500 flex-shrink-0" strokeWidth={2.5} />}
                        <HealthWarning
                          size="xs"
                          testId={`spa-ops-week-health-warn-${b.id}`}
                          guests={[
                            { name: b.primary?.guest_name, snapshot: b.primary?.health_snapshot },
                            { name: b.partner?.guest_name, snapshot: b.partner?.health_snapshot },
                          ]}
                        />
                        <span className="truncate">{startLabel} {b.guest_name}</span>
                      </div>
                      <div className="truncate text-muted-foreground text-[9px]">{b.treatment_name}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------- List view ----------
function ListView({ items, canSeePhotos, crew, ack: _ack, isNew, acknowledge, onOpenActions, compact = false, appTz = null }) {
  return (
    <ul className={compact ? "space-y-2" : "space-y-3"} data-testid="spa-ops-list">
      {items.map((b) => {
        const highlighted = isNew(b);
        return (
          <li
            key={b.id}
            onClick={() => onOpenActions ? onOpenActions(b) : (highlighted && acknowledge(b))}
            className={`group relative border ${compact ? "p-3" : "p-5"} flex items-start gap-4 transition-all cursor-pointer ${
              highlighted
                ? "border-accent bg-accent/10 shadow-[0_0_0_4px_rgba(198,168,106,0.15)] animate-pulse"
                : b.isCouple
                  ? "border-accent/40 bg-accent/5"
                  : "bg-card border-border hover:border-primary/40"
            } ${b.status === "cancelled" ? "opacity-40 line-through" : ""}`}
            data-testid={`spa-ops-appt-${b.id}`}
            data-highlighted={highlighted ? "true" : "false"}
          >
            {highlighted && (
              <span className="absolute top-2 right-2 text-[10px] uppercase tracking-widest text-accent font-medium flex items-center gap-1" data-testid={`spa-ops-new-badge-${b.id}`}>
                <Bell className="h-3 w-3" /> Awaiting confirmation
              </span>
            )}
            {b.confirmed_at && (
              <span className="absolute top-2 right-2 text-[10px] uppercase tracking-widest text-emerald-600 dark:text-emerald-400 font-medium flex items-center gap-1" data-testid={`spa-ops-confirmed-${b.id}`}>
                <Check className="h-3 w-3" /> {b.confirmed_by_name || "Confirmed"}
              </span>
            )}
            <div className={`text-center ${compact ? "min-w-[80px]" : "min-w-[110px]"} pt-1`}>
              <div className={`font-display ${compact ? "text-2xl" : "text-3xl"} text-primary`}>{fmtTime(b.start_at, appTz)}</div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-widest">{b.duration_min} min</div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                {b.isCouple && <Heart className="h-3.5 w-3.5 text-accent" strokeWidth={2.5} />}
                {canSeePhotos && (
                  <span className="flex -space-x-2 mr-1">
                    {[b.primary, b.partner].filter(Boolean).map((g) => (
                      g?.guest_image_path ? (
                        <img key={g.id} src={fileUrl(g.guest_image_path)} alt={g.guest_name} className={`${compact ? "w-7 h-7" : "w-9 h-9"} rounded-full object-cover border-2 border-card`} />
                      ) : (
                        <span key={g.id} className={`${compact ? "w-7 h-7" : "w-9 h-9"} rounded-full bg-muted border-2 border-card flex items-center justify-center`} title={g.guest_name}>
                          <User className="h-4 w-4 text-muted-foreground" />
                        </span>
                      )
                    ))}
                  </span>
                )}
                <span className={`font-medium ${compact ? "text-base" : "text-lg"}`}>{b.guest_name}</span>
                <HealthWarning
                  size={compact ? "sm" : "md"}
                  testId={`spa-ops-list-health-warn-${b.id}`}
                  guests={[
                    { name: b.primary?.guest_name, snapshot: b.primary?.health_snapshot },
                    { name: b.partner?.guest_name, snapshot: b.partner?.health_snapshot },
                  ]}
                />
              </div>
              <div className={`${compact ? "text-xs" : "text-sm"} text-foreground/80`}>
                {b.treatment_name}
                {b.crew_id && (
                  <span className="text-muted-foreground"> · with {crew.find((c) => c.id === b.crew_id)?.name || "Any"}</span>
                )}
              </div>
              {b.notes && !compact && (
                <div className="mt-2 text-xs text-muted-foreground flex items-start gap-1">
                  <MessageSquare className="h-3 w-3 mt-0.5 flex-shrink-0 text-accent" />
                  <span className="whitespace-pre-wrap">{b.notes}</span>
                </div>
              )}
            </div>
            {highlighted && !compact && (
              <Button
                onClick={(e) => { e.stopPropagation(); onOpenActions ? onOpenActions(b) : acknowledge(b); }}
                className="bg-accent hover:bg-accent/90 text-primary rounded-full"
                data-testid={`spa-ops-ack-${b.id}`}
              >
                <Check className="h-4 w-4 mr-2" /> Confirm
              </Button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

// ---------- Calendar view (compact hourly grid) ----------
function CalendarView({ day, items, canSeePhotos, crew, isNew, acknowledge, onOpenActions, now, isToday, compact = false, canQuickBook, onSlotClick, canEdit, onReschedule, prepMin = 0, appTz = null }) {
  // Position each appointment inside its hour row. Height proportional to duration.
  const nowPct = (() => {
    const h = now.getHours(); const m = now.getMinutes();
    const first = HOURS[0]; const last = HOURS[HOURS.length - 1] + 1;
    const total = (last - first) * 60;
    const cur = (h - first) * 60 + m;
    return Math.max(0, Math.min(100, (cur / total) * 100));
  })();
  const rowHeight = compact ? 56 : 72; // px per hour
  const halfHeight = rowHeight / 2;
  const totalHeight = HOURS.length * rowHeight;
  const first = HOURS[0];
  // 30-min slot descriptors — used for the per-cell "+" hotspots that pre-fill
  // the exact hour:minute in the on-the-spot booking dialog.
  const slots = [];
  HOURS.forEach((h) => { slots.push({ hour: h, minute: 0 }); slots.push({ hour: h, minute: 30 }); });
  // Hide a hotspot when the 30-min slot overlaps an existing (non-cancelled)
  // tile OR falls inside the prep-time buffer of any surrounding booking. The
  // buffer expands each busy interval by `prepMin` on both sides so operators
  // can't book a walk-in into the therapist's turnaround time — matches the
  // guest-side cabin iPad's slot picker.
  const isSlotBusy = (hour, minute) => {
    const slotStart = new Date(day || new Date()); slotStart.setHours(hour, minute, 0, 0);
    const slotEnd = new Date(slotStart.getTime() + 30 * 60000);
    const buf = Number(prepMin) * 60000;
    return items.some((b) => {
      if (b.status === "cancelled") return false;
      const s = new Date(b.start_at).getTime() - buf;
      const e = (b.end_at ? new Date(b.end_at).getTime() : new Date(b.start_at).getTime() + (b.duration_min || 60) * 60000) + buf;
      return s < slotEnd.getTime() && e > slotStart.getTime();
    });
  };

  return (
    <div className="bg-card border border-border" data-testid="spa-ops-calendar">
      <div className="grid" style={{ gridTemplateColumns: "56px 1fr" }}>
        {/* Hour labels + timeline area */}
        <div className="border-r border-border">
          {HOURS.map((h) => (
            <div key={h} className="text-[10px] tracking-widest uppercase text-muted-foreground text-right pr-2 relative"
                 style={{ height: rowHeight }}>
              <span className="absolute -top-1.5 right-2 bg-card px-1">{String(h).padStart(2, "0")}:00</span>
            </div>
          ))}
        </div>
        <div
          className="relative"
          style={{ height: totalHeight }}
          data-testid="spa-ops-cal-grid"
        >
          {/* Hour grid lines */}
          {HOURS.map((h, i) => (
            <div key={h} className="absolute left-0 right-0 border-t border-border/50" style={{ top: i * rowHeight }} />
          ))}
          {/* Per-30-min tap hotspots — a subtle dashed "+" is ALWAYS visible so
              touch-only devices (iPad) don't rely on :hover; the plus lifts to
              full opacity + a soft accent tint on hover / active. */}
          {canQuickBook && slots.map(({ hour, minute }) => {
            if (isSlotBusy(hour, minute)) return null;
            const topPx = (hour - first) * rowHeight + (minute === 30 ? halfHeight : 0);
            return (
              <button
                key={`hs-${hour}-${minute}`}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const when = new Date(day || new Date());
                  when.setHours(hour, minute, 0, 0);
                  onSlotClick && onSlotClick(when);
                }}
                className="absolute left-1 right-1 border border-dashed border-accent/25 hover:border-accent hover:bg-accent/10 active:bg-accent/25 flex items-center justify-center text-accent/50 hover:text-accent active:text-accent transition-all text-3xl font-light"
                style={{ top: topPx + 1, height: halfHeight - 2 }}
                aria-label={`Book at ${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`}
                title={`Book at ${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`}
                data-testid={`spa-ops-cal-slot-${hour}-${minute}`}
              >
                +
              </button>
            );
          })}
          {/* Drop zone — captures dragged tiles anywhere on the column so
              operators can drop into empty airspace as well as onto the
              slot buttons. Snaps to nearest 5 min. */}
          <div
            className="absolute inset-0 pointer-events-none"
            onDragOver={(e) => { if (canEdit) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}}
            onDrop={(e) => {
              if (!canEdit || !onReschedule) return;
              e.preventDefault();
              // Safari-compat: read text/plain first (see week-view above).
              const bid = e.dataTransfer.getData("text/plain") || e.dataTransfer.getData("text/booking-id");
              if (!bid) return;
              const when = slotDateFromClick(e, day, HOURS[0], rowHeight, appTz);
              onReschedule(bid, when);
            }}
            style={{ pointerEvents: canEdit ? "auto" : "none", zIndex: 0 }}
          />
          {/* Now line */}
          {isToday && (
            <div className="absolute left-0 right-0 z-10 pointer-events-none" style={{ top: `${nowPct}%` }} data-testid="spa-ops-now-line">
              <div className="h-px bg-destructive/70" />
              <div className="absolute -left-1 -top-1 h-2 w-2 rounded-full bg-destructive" />
            </div>
          )}
          {/* Appointments */}
          {items.map((b) => {
            // See WeekCalendarView above — using the app timezone here keeps
            // the tile's vertical offset in sync with the day the calendar
            // is rendering.
            const { hour: h, minute: m } = hmInTz(b.start_at, appTz);
            if (h < first || h > HOURS[HOURS.length - 1]) return null;
            const topPx = (h - first) * rowHeight + (m / 60) * rowHeight;
            const heightPx = Math.max(24, ((b.duration_min || 60) / 60) * rowHeight - 2);
            const highlighted = isNew(b);
            const cancelled = b.status === "cancelled";
            const confirmed = !!b.confirmed_at;
            const startLabel = fmtTime(b.start_at, appTz);
            return (
              <button
                key={b.id}
                draggable={canEdit && !cancelled}
                onDragStart={(e) => {
                  // Safari-compat: primary type must be `text/plain`.
                  e.dataTransfer.setData("text/plain", b.id);
                  e.dataTransfer.setData("text/booking-id", b.id);
                  e.dataTransfer.effectAllowed = "move";
                }}
                onClick={(e) => { e.stopPropagation(); onOpenActions ? onOpenActions(b) : (highlighted && acknowledge(b)); }}
                className={`absolute left-1 right-1 border text-left overflow-hidden transition-all z-10 ${
                  highlighted
                    ? "border-accent bg-accent/20 animate-pulse"
                    : confirmed
                      ? "border-emerald-500/40 bg-emerald-500/5 hover:border-emerald-500/70"
                      : b.isCouple
                        ? "border-accent/50 bg-accent/10"
                        : "border-primary/30 bg-primary/5 hover:border-primary/60"
                } ${cancelled ? "opacity-30 line-through" : ""} ${canEdit && !cancelled ? "cursor-move" : "cursor-pointer"}`}
                style={{ top: topPx + 1, height: heightPx }}
                data-testid={`spa-ops-cal-appt-${b.id}`}
                data-highlighted={highlighted ? "true" : "false"}
                title={`${b.guest_name} · ${b.treatment_name}${confirmed ? ` · Confirmed by ${b.confirmed_by_name}` : ""}`}
              >
                <div className={`px-2 py-1 text-[11px] leading-tight ${compact ? "" : "text-xs"}`}>
                  <div className="font-medium truncate flex items-center gap-1">
                    {b.isCouple && <Heart className="h-3 w-3 text-accent flex-shrink-0" strokeWidth={2.5} />}
                    {confirmed && <Check className="h-3 w-3 text-emerald-500 flex-shrink-0" strokeWidth={2.5} />}
                    <HealthWarning
                      size="xs"
                      testId={`spa-ops-cal-health-warn-${b.id}`}
                      guests={[
                        { name: b.primary?.guest_name, snapshot: b.primary?.health_snapshot },
                        { name: b.partner?.guest_name, snapshot: b.partner?.health_snapshot },
                      ]}
                    />
                    {canSeePhotos && b.primary?.guest_image_path && (
                      <img src={fileUrl(b.primary.guest_image_path)} alt="" className="w-4 h-4 rounded-full object-cover" />
                    )}
                    <span className="truncate">{startLabel} · {b.guest_name}</span>
                  </div>
                  <div className="truncate text-muted-foreground">{b.treatment_name}{b.crew_id ? ` · ${crew.find((c) => c.id === b.crew_id)?.name || ""}` : ""}</div>
                  {confirmed && !compact && (
                    <div className="truncate text-[9px] text-emerald-600 dark:text-emerald-400 mt-0.5">✓ {b.confirmed_by_name}</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

